/**
 * Listing product card — colour swatch updates image, title, price, specs, and quick-add.
 */
(() => {
  if (window.__themeProductCard) return;
  window.__themeProductCard = true;

  const setTextIfChanged = (element, value) => {
    if (!element || value == null || element.textContent === value) return;
    element.textContent = value;
  };

  const setAttrIfChanged = (element, name, value) => {
    if (!element || value == null) return;
    if (element.getAttribute(name) === value) return;
    element.setAttribute(name, value);
  };

  const setHiddenIfChanged = (element, hidden) => {
    if (!(element instanceof HTMLElement)) return;
    if (element.hidden === hidden) return;
    element.hidden = hidden;
  };

  const setHrefIfChanged = (element, url) => {
    if (!(element instanceof HTMLAnchorElement) || !url) return;
    if (element.getAttribute('href') === url) return;
    element.setAttribute('href', url);
  };

  const updateImage = (image, url, srcset, alt) => {
    if (!(image instanceof HTMLImageElement)) return;

    if (url && image.getAttribute('src') !== url) {
      image.src = url;
    }

    if (srcset && image.getAttribute('srcset') !== srcset) {
      image.srcset = srcset;
    }

    if (alt != null && image.getAttribute('alt') !== alt) {
      image.alt = alt;
    }
  };

  const updateHover = (card, swatch) => {
    const hoverImage = card.querySelector('.product-card__image--hover');
    const hoverUrl = swatch.getAttribute('data-hover-image-url');
    const hoverSrcset = swatch.getAttribute('data-hover-image-srcset');
    const hasHover = Boolean(hoverUrl);

    if (hoverImage instanceof HTMLImageElement) {
      if (hasHover) {
        updateImage(hoverImage, hoverUrl, hoverSrcset);
      }

      setHiddenIfChanged(hoverImage, !hasHover);
    }

    card.classList.toggle(
      'product-card--has-hover',
      hasHover && hoverImage instanceof HTMLImageElement
    );
  };

  const updateLinks = (card, swatch) => {
    const nextUrl = swatch.getAttribute('data-url');
    const nextTitle = swatch.getAttribute('data-title');
    const titleLink = card.querySelector('[data-product-card-title]');

    // Formatted titles are product-level. A colour swatch shares that title,
    // and textContent would drop the bold.
    if (nextTitle && !titleLink?.hasAttribute('data-formatted-title')) {
      setTextIfChanged(titleLink, nextTitle);
    }

    setHrefIfChanged(titleLink, nextUrl);
    setHrefIfChanged(card.querySelector('.product-card__media-link'), nextUrl);
    setHrefIfChanged(card.querySelector('.product-card__reviews a'), nextUrl);
  };

  const updatePrice = (card, swatch) => {
    const nextPrice = swatch.getAttribute('data-price');
    const nextCompare = swatch.getAttribute('data-compare-price');
    const compareActive = swatch.getAttribute('data-compare-active') === 'true';
    const compare = card.querySelector('[data-product-card-compare]');
    const compareAmount = card.querySelector('[data-product-card-compare-price]');
    const current = card.querySelector('[data-product-card-current]');

    if (nextPrice) {
      setTextIfChanged(card.querySelector('[data-product-card-current-price]'), nextPrice);
    }

    const onSale = compareActive && Boolean(nextCompare) && Boolean(compareAmount);

    current?.classList.toggle('product-card__price-current--sale', onSale);
    setHiddenIfChanged(card.querySelector('[data-product-card-price-label="sale"]'), !onSale);
    setHiddenIfChanged(card.querySelector('[data-product-card-price-label="regular"]'), onSale);

    if (!(compare instanceof HTMLElement)) return;

    if (onSale) {
      setTextIfChanged(compareAmount, nextCompare);
      setHiddenIfChanged(compare, false);
      return;
    }

    setHiddenIfChanged(compare, true);
  };

  const updateSpecs = (card, swatch) => {
    const specRows = card.querySelectorAll('[data-product-card-spec]');
    const specs = card.querySelector('.product-card__specs');
    let visibleSpecs = 0;

    for (const row of specRows) {
      if (!(row instanceof HTMLElement)) continue;

      const key = row.getAttribute('data-product-card-spec');
      const value = key ? swatch.getAttribute(`data-spec-${key}`) || '' : '';
      const empty = value === '';

      setTextIfChanged(row.querySelector('.product-card__spec-value'), value);
      setHiddenIfChanged(row, empty);

      if (!empty) visibleSpecs += 1;
    }

    if (specs instanceof HTMLElement) {
      setHiddenIfChanged(specs, visibleSpecs === 0);
    }
  };

  // Opt-out, pre-order, and quick-add-off products render a link instead of a
  // button. A sold-out colour still has to mute that link when another colour
  // can be bought. A product that is sold out entirely keeps the link so the
  // back-in-stock form stays reachable.
  const updateQuickAddLink = (card, link, swatch) => {
    const available = swatch.getAttribute('data-available') === 'true';
    const nextUrl = swatch.getAttribute('data-url');
    const productAvailable = card.getAttribute('data-product-available') === 'true';
    const block = productAvailable && !available;

    link.classList.toggle('quick-add__button--sold-out', !available);

    if (block) {
      link.removeAttribute('href');
      link.setAttribute('role', 'link');
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');
      return;
    }

    link.removeAttribute('role');
    link.removeAttribute('aria-disabled');
    link.removeAttribute('tabindex');

    if (nextUrl) {
      setHrefIfChanged(link, nextUrl);
    }
  };

  const updateQuickAdd = (card, swatch) => {
    const control = card.querySelector('.quick-add__button');
    const variantId = swatch.getAttribute('data-variant-id');
    const nextUrl = swatch.getAttribute('data-url');
    const nextTitle = swatch.getAttribute('data-title');
    const available = swatch.getAttribute('data-available') === 'true';

    if (control instanceof HTMLAnchorElement) {
      updateQuickAddLink(card, control, swatch);
      return;
    }

    if (!(control instanceof HTMLButtonElement)) return;

    const button = control;

    if (button.hasAttribute('data-quick-add-drawer')) {
      if (variantId) {
        setAttrIfChanged(button, 'data-variant-id', variantId);
      }

      if (nextUrl) {
        setAttrIfChanged(button, 'data-product-url', nextUrl);
      }

      if (nextTitle) {
        const template = button.getAttribute('data-drawer-label-template') || '';
        const nextLabel = template ? template.replace('__product__', nextTitle) : '';

        if (nextLabel) {
          setAttrIfChanged(button, 'aria-label', nextLabel);
        }
      }

      const productAvailable =
        button.getAttribute('data-product-available') === 'true';

      // The drawer opens for the selected colour, so a sold-out swatch has to
      // disable it even when another colour of the product is in stock.
      const drawerSellable = productAvailable && Boolean(variantId) && available;

      if (button.disabled !== !drawerSellable) {
        button.disabled = !drawerSellable;
      }

      setAttrIfChanged(
        button,
        'aria-disabled',
        drawerSellable ? 'false' : 'true'
      );
      return;
    }

    // A colour with no matching variant leaves data-variant-id empty. The
    // button has to stop being addable rather than keep the previous variant.
    const sellable = Boolean(variantId) && available;

    if (variantId) {
      setAttrIfChanged(button, 'data-variant-id', variantId);
    }

    if (button.disabled !== !sellable) {
      button.disabled = !sellable;
    }

    setAttrIfChanged(button, 'aria-disabled', sellable ? 'false' : 'true');
  };

  const selectSwatch = (card, swatch) => {
    const swatches = card.querySelectorAll('[data-product-card-swatch]');

    for (const item of swatches) {
      const selected = item === swatch;
      item.classList.toggle('product-card__swatch--selected', selected);
      item.setAttribute('aria-checked', selected ? 'true' : 'false');
    }

    updateImage(
      card.querySelector('.product-card__image--default'),
      swatch.getAttribute('data-image-url'),
      swatch.getAttribute('data-image-srcset'),
      swatch.getAttribute('data-image-alt')
    );
    updateHover(card, swatch);
    updateLinks(card, swatch);
    updatePrice(card, swatch);
    updateSpecs(card, swatch);
    updateQuickAdd(card, swatch);
    card.dispatchEvent(
      new CustomEvent('product-card:variant-change', {
        bubbles: true,
        detail: { variantId: swatch.getAttribute('data-variant-id') },
      })
    );
  };

  const swatchFromEvent = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const swatch = target ? target.closest('[data-product-card-swatch]') : null;

    if (!(swatch instanceof HTMLElement)) return null;

    const card = swatch.closest('[data-product-card]');

    return card instanceof HTMLElement ? { card, swatch } : null;
  };

  const ROVING_KEYS = new Set(['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End']);

  const swatchToFocus = (card, swatch, key) => {
    const swatches = [...card.querySelectorAll('[data-product-card-swatch]')];
    const index = swatches.indexOf(swatch);

    if (index === -1 || swatches.length < 2) return null;

    if (key === 'Home') return swatches[0];
    if (key === 'End') return swatches[swatches.length - 1];

    const delta = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1;

    return swatches[(index + delta + swatches.length) % swatches.length];
  };

  document.addEventListener('click', (event) => {
    const found = swatchFromEvent(event);

    if (!found) return;

    event.preventDefault();
    selectSwatch(found.card, found.swatch);
  });

  // Swatches are a radiogroup, so arrows move between them and select as they go.
  document.addEventListener('keydown', (event) => {
    if (!ROVING_KEYS.has(event.key)) return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

    const found = swatchFromEvent(event);

    if (!found) return;

    const next = swatchToFocus(found.card, found.swatch, event.key);

    if (!(next instanceof HTMLElement)) return;

    event.preventDefault();
    next.focus();
    selectSwatch(found.card, next);
  });
})();
